// Authenticated backend API client.
// Attaches the current Supabase session's access token as a Bearer header.
// All backend write routes require this — the backend verifies the JWT via supabaseAuth.
import { supabase, BACKEND_URL } from './supabase';
export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly details?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}
async function getAccessToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new ApiError(401, 'not_signed_in');
  return token;
}
export async function apiGet<T>(path: string): Promise<T> {
  const token = await getAccessToken();
  const res = await fetch(`${BACKEND_URL}${path}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({ error: 'unknown' }));
    throw new ApiError(res.status, payload.error ?? res.statusText, payload);
  }
  return (await res.json()) as T;
}
export async function apiPost<T, B = unknown>(path: string, body: B): Promise<T> {
  const token = await getAccessToken();
  const res = await fetch(`${BACKEND_URL}${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({ error: 'unknown' }));
    throw new ApiError(res.status, payload.error ?? res.statusText, payload);
  }
  return (await res.json()) as T;
}
// Phase 5a: PATCH /api/conversations/:id wires up status transitions
// (open/awaiting/snoozed/resolved/spam). Mirrors apiPost exactly except for
// the HTTP method.
export async function apiPatch<T, B = unknown>(path: string, body: B): Promise<T> {
  const token = await getAccessToken();
  const res = await fetch(`${BACKEND_URL}${path}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({ error: 'unknown' }));
    throw new ApiError(res.status, payload.error ?? res.statusText, payload);
  }
  return (await res.json()) as T;
}

// Polish #18: DELETE /api/attachments/:id + /api/proofs/:id for the composer
// unstage flow and proof-untrack flow.
export async function apiDelete<T>(path: string): Promise<T> {
  const token = await getAccessToken();
  const res = await fetch(`${BACKEND_URL}${path}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({ error: 'unknown' }));
    throw new ApiError(res.status, payload.error ?? res.statusText, payload);
  }
  return (await res.json()) as T;
}
